import { NavLink, useMatch } from "react-router-dom";
import { cn } from "@/lib/utils";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";

type NavItemProps = {
  to: string;
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  /**
   * Forwarded to NavLink. When true, the link is only active for the exact
   * path — needed for parents that share a prefix with a child route
   * (e.g. /account would otherwise dual-highlight on /account/billing).
   */
  end?: boolean;
  /**
   * When true, hide the text label and show it as a hover tooltip instead.
   * Used by the collapsed-sidebar icon rail.
   */
  collapsed?: boolean;
};

export function NavItem({ to, icon: Icon, label, end, collapsed }: NavItemProps) {
  // Compute isActive ourselves rather than using NavLink's render-prop
  // className. When collapsed, we wrap NavLink in <TooltipTrigger asChild>,
  // and Radix's Slot stringifies prop functions with join(" ") when merging
  // — that turns a className function into the literal source of the
  // function and strips all styling from every link. Passing a plain string
  // sidesteps the merge problem and keeps the active highlight visible.
  const match = useMatch({ path: to, end: !!end });
  const isActive = match !== null;

  const className = cn(
    "flex items-center gap-3 rounded-md py-2 ps-2 pe-3 text-sm transition-colors",
    // The left border is the active-state accent in LTR. Under RTL we keep
    // the accent on the start edge so it still hugs the sidebar's inner
    // edge — Tailwind's `border-s-*` is the logical equivalent.
    "border-s-4 border-s-transparent",
    "hover:bg-primary/10 hover:text-foreground",
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
    collapsed && "justify-center pe-2",
    isActive
      ? "border-s-primary bg-accent text-primary font-medium"
      : "text-muted-foreground",
  );

  const link = (
    <NavLink
      to={to}
      end={end}
      aria-label={collapsed ? label : undefined}
      className={className}
    >
      <Icon className="h-5 w-5 shrink-0" />
      {!collapsed && <span>{label}</span>}
    </NavLink>
  );

  if (!collapsed) return link;
  return (
    <Tooltip>
      <TooltipTrigger asChild>{link}</TooltipTrigger>
      <TooltipContent side="right">{label}</TooltipContent>
    </Tooltip>
  );
}
